import $ from 'jquery';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { FormControl, Validators, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';

import { Session } from '../services/session';
import { CustomValidators } from '../services/custom-validators';
import { ReferralModal, IReferralsParams } from '../services/referral-modal';
import { KEY_CODES } from '../lib/key-codes';

@Component({
  selector: 'referral-modal',
  templateUrl: './referral-modal.component.html',
  styleUrls: ['./referral-modal.component.scss'],
})
export class ReferralModalComponent implements OnDestroy, AfterViewInit {
  public entityEmailControl: FormControl;
  public entityMessageControl: FormControl;
  public isPreparing: boolean;
  public isEditable: boolean;
  public isPersonalizeMessage: boolean;
  public userHasEmail: boolean;
  public emails = [];
  public referLink: string;
  public inviteSent: boolean;
  public isInputEmailSeparator: boolean;
  private el: HTMLElement;
  private $modal: any;
  private readonly syncValidators: ValidatorFn[] = [
    Validators.required, this.customValidators.isBlank(), this.customValidators.emailFormat(),
  ];

  constructor(
    el: ElementRef,
    private referralModalService: ReferralModal,
    private route: Router,
    @Inject(Session) public session: Session,
    @Inject(ReferralModal) referralModal,
    @Inject(CustomValidators) private customValidators: CustomValidators,
  ) {
    if (this.isSpecificRoute('/earn-cash')) {
      this.referralModalService.show();
    }

    this.el = el.nativeElement;
    referralModal.registerComponent(this);
    this.userHasEmail = session.userHasEmail();
    this.isPreparing = true;
    this.isInputEmailSeparator = false;

    this.entityEmailControl = new FormControl('', [...this.syncValidators]);
    this.entityMessageControl = new FormControl();
  }

  public onKey(e: KeyboardEvent) {
    const email = this.entityEmailControl.value;

    this.isInputEmailSeparator = e.keyCode === KEY_CODES.Comma || e.keyCode === KEY_CODES.Enter;

    if (this.isInputEmailSeparator) {
      if (this.entityEmailControl.valid) {
        this.emails.push(email);
        this.entityEmailControl.setValue('');
        this.resetValidations();
      } else {
        // to make sure error appears in case comma is input
        // and email is invalid
        this.isInputEmailSeparator = false;
      }
    } else if (e.keyCode === KEY_CODES.Backspace && email.length === 0) {
      this.emails.pop();
    }

    this.$modal.modal('refresh');
  }

  public removeComma(e: KeyboardEvent) {
    const email = this.entityEmailControl.value;

    if (this.isInputEmailSeparator && email.length === 1) {
      this.resetValidations();
      this.entityEmailControl.setValue('');
    } else if (this.emails.length > 0 && email.length === 0) {
      this.resetValidations();
    }
  }

  public removeEmail(i: number) {
    const filteredItems = this.emails.slice(0, i).concat(this.emails.slice(i + 1, this.emails.length));
    this.emails = filteredItems;
  }

  public ngAfterViewInit() {
    this.$modal = $(this.el.querySelector('.ui.modal'));
    this.$modal.modal({
      onHidden: () => {
        this.close();
      },
    });
  }

  public ngOnDestroy() {
    this.$modal.modal('destroy');
    this.$modal.remove();
  }

  public close() {
    this.resetValidations();
    this.removeCustomValidators();
    this.isPreparing = true;
    this.inviteSent = false;
    this.isEditable = false;
    this.isPersonalizeMessage = false;
    this.emails = [];
    this.entityEmailControl.setValue('');
    this.entityMessageControl.setValue('');
    this.$modal.modal('hide');

    if (this.isSpecificRoute('/earn-cash')) {
      // navigate to fleet view
      this.route.navigate(['']);
    }
  }

  public save() {
    if ( this.entityEmailControl.valid ) this.emails.push(this.entityEmailControl.value);
    if (this.emails.length === 0) return;
    const message = this.entityMessageControl.value;
    const params: IReferralsParams[] = [];

    this.emails.forEach(email => {
      params.push({
        email,
        ambassadorCustomMessage: message,
      });
    });

    this.referralModalService.saveReferralsToMarketo(params).subscribe( res => res );

    this.inviteSent = true;
    setTimeout(() => {
      this.close();
      this.inviteSent = false;
    }, 3000);
  }

  public show(customSyncValidatorsList: ValidatorFn[] = []) {
    if (!customSyncValidatorsList) customSyncValidatorsList = [];
    this.referralModalService.get().subscribe(res => {
      this.referLink = res.referrer_link;
      this.isPreparing = false;
    });
    this.entityEmailControl.setValue('');
    this.entityMessageControl.setValue('');
    this.entityEmailControl.setValidators([...this.syncValidators, ...customSyncValidatorsList]);
    this.resetValidations();
    this.$modal.modal('show');
  }

  public personalizeMessage() {
    this.isPersonalizeMessage = true;
  }

  public focusInput(e) {
    this.$modal.find('input.email').focus();
    e.stopPropagation();
  }

  private resetValidations(): void {
    this.entityEmailControl.markAsPristine();
    this.entityEmailControl.markAsUntouched();
  }

  private removeCustomValidators(): void {
    this.entityEmailControl.setValidators(this.syncValidators);
  }

  private isSpecificRoute(path: string): boolean {
    return this.route.url.startsWith(path);
  }
}
